import { BIT_BY_BIT, DFE_TYPE, ENABLE_CTLE_OR_FFE } from "../../ADS/constant";
import { unitChange } from "../../helper/mathHelper";
import { CONTROLLER, MEMORY } from "../../PCBHelper";
import { getJittersList } from "./amiDisplayHelper";
import { getJitterEQDefaultValue, MAXIMAL_LENGTH_LFSR, USER_DEFINED_LFSR, USER_DEFINED_SEQUENCE } from "./constant";
import _ from 'lodash'

const DRIVER = "Driver", IBIS = "IBIS", IBIS_AMI = "IBIS_AMI";

function ADSSimulationConfig({ type, numberOfBits, lowBERFloor = true, statusLevel,
  toleranceMode, enforcePassivity = true, impLFEOn = true, impCache = true,
  maxImpulseLength, impMaxFreq, numberTimePtPerUI, blockSize, antiAliasingWindow, enforceStrictPassivity, memoryProbe = {}, dqEyeMaskLibraryId, dqReadEyeMaskLibraryId, caEyeMaskLibraryId }) {
  this.type = type || BIT_BY_BIT;

  // Analysis
  this.lowBERFloor = lowBERFloor;
  this.statusLevel = statusLevel || '2';
  if (this.type === BIT_BY_BIT) {
    this.numberOfBits = numberOfBits || 1000;
    this.blockSize = blockSize || '1024'
  }

  // Convolution
  this.toleranceMode = toleranceMode || "1";
  this.enforcePassivity = enforcePassivity;
  this.impLFEOn = impLFEOn;
  this.impCache = impCache

  // Convolution advanced
  this.maxImpulseLength = maxImpulseLength || '100';
  this.impMaxFreq = impMaxFreq || '';
  this.numberTimePtPerUI = numberTimePtPerUI || '32';
  this.antiAliasingWindow = antiAliasingWindow || '1';
  this.enforceStrictPassivity = enforceStrictPassivity || false

  // Memory probe -> Measurement
  this.memoryProbe = new SimulationMemoryProbeConfig(memoryProbe);

  //eye mask
  this.dqEyeMaskLibraryId = dqEyeMaskLibraryId || "";
  this.dqReadEyeMaskLibraryId = dqReadEyeMaskLibraryId || "";
  this.caEyeMaskLibraryId = caEyeMaskLibraryId || "";
}

function SimulationMemoryProbeConfig({ enableBERList = true, berList = ["1e-12", "1e-11", "1e-10"], targetBER = "1e-16", extrapolateBER = true }) {
  this.enableBERList = enableBERList;
  this.berList = berList
  this.targetBER = targetBER;
  this.extrapolateBER = extrapolateBER
}

//Driver
// IBIS
//    There is EQ
// IBIS_AMI
//    Without EQ
// Receiver
//  IBIS
//  IBIS_AMI
// usePackage, setAllData = true, interpolationMode, jitters = [], EQ, saveCharacterizationResult, addDCOffsetToOutput = true

function ADSModelConfig({ usage, ads = {}, isDefault = false, modelLibType }) {
  if (![IBIS, IBIS_AMI].includes(modelLibType)) { return }
  const { setAllData = true, interpolationMode, jitters = [], EQ } = ads

  // this.setAllData = isDefault ? true : setAllData;
  this.interpolationMode = interpolationMode && !isDefault ? interpolationMode : "spline";

  const jitterList = getJittersList(usage, modelLibType)
  this.jitters = jitters && jitters.length && !isDefault ? jitters : jitterList.map(item => getJitterEQDefaultValue(item));

  if (usage === 'Driver') { // TX
    if (modelLibType === IBIS) {
      this.EQ = EQ && !isDefault ? EQ : { type: "None" }
      // } else if (modelLibType === IBIS_AMI) {
      // Save Out/lnOut parameters(TX)
      // Number of time points per Ul (TX)
      // Custom number of time points per Ul (TX)
    }
  } else { // RX
    if (modelLibType === IBIS) {
      this.EQ = EQ && !isDefault ? EQ : { [ENABLE_CTLE_OR_FFE]: "None", [DFE_TYPE]: "None" }
      // } else if (modelLibType === IBIS_AMI) {
      // Save Out/lnOut parameters(RX)
      // Number of time points per Ul (RX)
      // Custom number of time points per Ul (RX)
    }
  }
}

function getADSModelConfig({ ads = {}, usage, modelLibType }) {
  let _ads = ads && Object.keys(ads) ? { ...ads, jitters: null, EQ: null } : {}
  const defaultConfig = new ADSModelConfig({ usage, ads: _ads, modelLibType })

  if (!ads || !Object.keys(ads)) {
    return defaultConfig
  }

  if ((ads.EQ && !defaultConfig.EQ) || (!ads.EQ && defaultConfig.EQ)) {
    return defaultConfig
  }

  const jitterKeys = ads.jitters && ads.jitters.length ? ads.jitters.map(item => item.name) : []
  const defaultJitterKeys = defaultConfig.jitters.map(item => item.name)
  if (!_.isEqual(jitterKeys, defaultJitterKeys)) {
    return defaultConfig
  }

  return ads;
}

function StimulusPRBSModelConfig({ mode, registerLength, taps, seed, bitSequence, delay }, isDiff) {
  this.mode = mode ? mode : isDiff ? USER_DEFINED_SEQUENCE : USER_DEFINED_LFSR;
  this.delay = delay ? delay : '0ps'
  if (this.mode === MAXIMAL_LENGTH_LFSR) {
    this.registerLength = registerLength || '8'
  } else if (this.mode === USER_DEFINED_LFSR) {
    this.taps = taps ? taps : isDiff ? '010101' : '01000100111011010';
    this.seed = seed ? seed : isDiff ? '010101' : '11010111011101010';
  } else if (this.mode === USER_DEFINED_SEQUENCE) {
    this.bitSequence = bitSequence ? bitSequence : isDiff ? '01010101' : '10101010';
  }
}

// Switch Simulator to ADS in simulation
function getSwitchSimulatorInfo(Interfaces, simulate) {
  let _Interfaces = JSON.parse(JSON.stringify(Interfaces));
  _Interfaces.forEach((info) => {
    let components = JSON.parse(JSON.stringify(info.Content.Components));
    components = components.filter(comp => !!comp);
    for (let i = 0; i < components.length; i++) {
      let comp = components[i];
      //ADS and adsStimulus need to be added to pins
      //Only when usage is the driver, there is adsStimulus available
      if ([CONTROLLER, MEMORY].includes(comp.type) && comp.pins && comp.pins.length) {
        comp.pins.forEach(item => {
          const { usage, model } = item;
          const modelLibType = model && model.libType ? model.libType : "IBIS";
          if (modelLibType && ['IBIS', "IBIS_AMI"].includes(modelLibType)) {
            const ads = new ADSModelConfig({ usage, ads: item.ads || {}, isDefault: true, modelLibType: modelLibType })
            item.ads = ads;
          }
          if (usage === DRIVER) {
            item.adsStimulus = new StimulusPRBSModelConfig(item.adsStimulus || {})
          }
        })
      }
    }
    info.Content.Components = components;
  })
  return _Interfaces;
}

function autoConvertTime(psValue) {
  const units = [
    { unit: 's', factor: 1e-12 },
    { unit: 'ms', factor: 1e-9 },
    { unit: 'μs', factor: 1e-6 },
    { unit: 'ns', factor: 1e-3 },
    { unit: 'ps', factor: 1 },
    { unit: 'fs', factor: 1e3 },
    { unit: 'as', factor: 1e6 }
  ];

  let bestUnit = units[units.length - 1]; // 默认 as

  for (let i = 0; i < units.length; i++) {
    const valueInUnit = psValue * units[i].factor;
    if (valueInUnit >= 1 && valueInUnit < 1000) {
      bestUnit = {
        value: valueInUnit,
        unit: units[i].unit
      };
      break;
    }
  }

  if (bestUnit.unit === 'as' && psValue >= 1) {
    bestUnit = {
      value: psValue,
      unit: 'ps'
    };
  }

  return formatOutput(bestUnit.value, bestUnit.unit);
}

function formatOutput(value, unit) {
  if (Number.isInteger(value)) {
    return `${value.toFixed(0)}${unit}`;
  }
  return `${value.toFixed(3).replace(/\.?0+$/, '')}${unit}`;
}
function getDefaultDelayValue(currentConfig, useCAClock) {
  let delayValue = 0, delayUnit = 'ps'
  let currentClock = currentConfig.clock
  if (useCAClock) {
    currentClock = currentConfig.caClock
  }
  const value = unitChange({ num: currentClock.value, oldUnit: currentClock && currentClock.unit ? `${currentClock.unit}Hz` : `MHz`, newUnit: 'MHz', decimals: -1 }).number * 2 * 1e-6
  // M // ps
  // if (useCAClock) {
  //   delayValue = (0.25 / value).toFixed(3)
  // } else {
  delayValue = (0.5 / value).toFixed(3)
  // }
  return `${delayValue}${delayUnit}`
}

function getDefaultDelayValueFormSignal(useCAClock, isDiffSignal, value) {
  if (!useCAClock && !isDiffSignal) {
    // DQ
    return '0ps'
  } else if (useCAClock && !isDiffSignal) {
    return `-${value}`
  }
  return value
}

export {
  ADSSimulationConfig,
  ADSModelConfig,
  StimulusPRBSModelConfig,
  SimulationMemoryProbeConfig,
  getSwitchSimulatorInfo,
  getADSModelConfig,
  getDefaultDelayValue,
  getDefaultDelayValueFormSignal
}